import React, { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { QRCodeSVG } from 'qrcode.react';

import { traceAction } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION } from '@/feedback/ontology/types';
import { API_URL, FRONTEND_URL } from '@/lib/consts';

import { DisplayConceptRef, PublicDocId } from '../lib/api';
import { RemoveTopicButton } from './RemoveButton';
import { TopicSelector } from './TopicSelector';

/**
 * Props for rendering invite creation in either the standalone `/invite` page or embedded flows.
 * The page and modal callers share the same request logic while opting into different success UI.
 */
export interface InviteFormProps {
  /** Associates the invite with a post/thread when present so post-specific copy can be shown. */
  threadContext?: PublicDocId;

  /** Optional form-only intro content; omitted by the standalone page because it already renders a heading. */
  introMessage?: React.ReactNode;

  /** Lets the parent react to successful invite creation without duplicating the request flow. */
  onInviteCreated?: (inviteCode: string, inviteUrl: string) => void;
  onCancel?: () => void;
  /** Enabled only on `/invite` so the QR does not render inside the ComposeBox modal flow. */
  showQrCode?: boolean;
}

/**
 * Request payload sent to the invite endpoint.
 * An omitted nickname yields a hash-only invite code without a user-provided prefix.
 */
export interface InviteFormData {
  inviterTopics: string[];
  inviteeTopics: string[];
  /** Included only when a trimmed nickname remains after client-side normalization. */
  nickname?: string;
  threadContext?: PublicDocId;
}

/**
 * Response from the invite-creation endpoint. The backend returns ad-hoc JSON
 * (`{ status, inviteCode }`) from `create_invite_code` in
 * rust/server/src/api/user.rs; only `inviteCode` is consumed here.
 */
interface CreateInviteResponse {
  inviteCode: string;
}

/**
 * Creates invites and renders either the form or the success state for the page and modal callers.
 * The component keeps invite URL generation, copy behavior, and success rendering in one place.
 */
export const InviteForm: React.FC<InviteFormProps> = ({
  threadContext,
  introMessage,
  onInviteCreated,
  onCancel,
  showQrCode = false,
}) => {
  const [inviterTopics, setInviterTopics] = useState<DisplayConceptRef[]>([]);
  const [inviteeTopics, setInviteeTopics] = useState<DisplayConceptRef[]>([]);
  const [inviteCode, setInviteCode] = useState<string>('');
  const [nickname, setNickname] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData: InviteFormData = {
        inviterTopics: inviterTopics.map((topic) => topic.cref),
        inviteeTopics: inviteeTopics.map((topic) => topic.cref),
      };

      const normalizedNickname = nickname.trim();
      if (normalizedNickname) {
        formData.nickname = normalizedNickname;
      }

      // Add threadContext if available
      if (threadContext) {
        formData.threadContext = threadContext;
      }

      // Feedback breadcrumb: record the invite-creation attempt and its success/failure outcome.
      const data = await traceAction(FEEDBACK_ACTION.CREATE_INVITE, async () => {
        const response = await fetch(`${API_URL}/auth/create_invite`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error('Failed to generate invite code');
        }

        return (await response.json()) as CreateInviteResponse;
      });
      setInviteCode(data.inviteCode);

      const inviteUrl = `${FRONTEND_URL}/register/${data.inviteCode}`;

      // Call the callback with the invite code and URL if provided
      if (onInviteCreated) {
        onInviteCreated(data.inviteCode, inviteUrl);
      }
    } catch (error) {
      console.error('Error generating invite code:', error);
      setError('Failed to generate invite code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTopicSelect = (topic: DisplayConceptRef | null, isFollowTopics: boolean) => {
    if (!topic) {
      return;
    }
    if (isFollowTopics) {
      setInviterTopics((prevTopics) => [...prevTopics, topic]);
    } else {
      setInviteeTopics((prevTopics) => [...prevTopics, topic]);
    }
  };

  const removeTopic = (index: number, isFollowTopics: boolean) => {
    if (isFollowTopics) {
      setInviterTopics((prevTopics) => prevTopics.filter((_, i) => i !== index));
    } else {
      setInviteeTopics((prevTopics) => prevTopics.filter((_, i) => i !== index));
    }
  };

  const inviteUrl = inviteCode ? `${FRONTEND_URL}/register/${inviteCode}` : '';

  return (
    <div className="invite-form-container">
      {!inviteCode && (introMessage || <p>Bring on a friend and share your expertise together!</p>)}

      {!inviteCode ? (
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <TextField
              id="nickname"
              label="A nickname to personalize this invite"
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              fullWidth
              variant="outlined"
            />
          </Box>
          <div role="group" aria-labelledby="following-topics-label" className="form-group">
            <label id="following-topics-label">
              (Optional) Topics you want to follow your friend on:
            </label>
            <TopicSelector
              instanceId="invite-following-topic-selector"
              onChangeFn={(topic: DisplayConceptRef | null) => handleTopicSelect(topic, true)}
              aria-label="Select topics you want to follow your friend on"
            />
            <List dense aria-label="Selected topics to follow your friend on" sx={{ mt: 1 }}>
              {inviterTopics.map((topic, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <RemoveTopicButton
                      topic={topic.display}
                      onRemove={() => removeTopic(index, true)}
                    />
                  }
                  sx={{ pl: 0 }}
                >
                  <ListItemText primary={topic.display} />
                </ListItem>
              ))}
            </List>
          </div>
          <div role="group" aria-labelledby="followed-topics-label" className="form-group">
            <label id="followed-topics-label">
              (Optional) Topics your friend might want to follow you on:
            </label>
            <TopicSelector
              instanceId="invite-followed-topic-selector"
              onChangeFn={(topic: DisplayConceptRef | null) => handleTopicSelect(topic, false)}
              aria-label="Select topics your friend might want to follow you on"
            />
            <List
              dense
              aria-label="Selected topics for your friend to follow you on"
              sx={{ mt: 1 }}
            >
              {inviteeTopics.map((topic, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <RemoveTopicButton
                      topic={topic.display}
                      onRemove={() => removeTopic(index, false)}
                    />
                  }
                  sx={{ pl: 0 }}
                >
                  <ListItemText primary={topic.display} />
                </ListItem>
              ))}
            </List>
          </div>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            {onCancel && (
              <Button type="button" onClick={onCancel} variant="outlined">
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting} variant="contained">
              {isSubmitting ? 'Creating...' : 'Create Invite'}
            </Button>
          </Box>
        </form>
      ) : (
        <Box component="section" className="invite-code" aria-live="polite">
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: '100%',
                textAlign: 'center',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              <Typography component="p" className="invite-code-text">
                <Box component="span" sx={{ fontWeight: 500 }}>
                  Invite Code:{' '}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    ml: 0.75,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    border: '1px solid var(--mui-palette-divider)',
                    backgroundColor: 'var(--mui-palette-background-paper)',
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    fontWeight: 600,
                    verticalAlign: 'baseline',
                  }}
                >
                  {inviteCode}
                </Box>
              </Typography>
            </Box>

            {showQrCode && (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: { xs: 280, sm: 320 },
                  mx: 'auto',
                  '& svg': {
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                  },
                }}
              >
                <QRCodeSVG value={inviteUrl} includeMargin />
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                flexWrap: 'wrap',
                width: '100%',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              <code className="invite-url">
                <Link href={inviteUrl} target="_blank" rel="noopener noreferrer">
                  {inviteUrl}
                </Link>
              </code>
              <IconButton
                aria-label="Copy invite link"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(inviteUrl);
                  } catch (error) {
                    console.error('Error copying invite link:', error);
                    setError('Failed to copy invite link. Please copy it manually.');
                  }
                }}
                size="small"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>

            {threadContext && (
              <Typography component="p" sx={{ textAlign: 'center' }}>
                After your friend signs up, they&apos;ll be directed to your post.
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, width: '100%' }}>
              {onCancel && (
                <Button onClick={onCancel} variant="contained">
                  Done
                </Button>
              )}
            </Box>
          </Stack>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </div>
  );
};
